import * as React from 'react';
import { tenantConfig } from 'utils/tenant';
import { useError } from 'hooks/useError';
import { Outlet } from 'react-router';
import { EErrorCode } from 'config/errors-codes';
import { user } from 'utils/auth/user';
import { isAuthenticated } from 'utils/auth';
import { applyDealerIdFromUrl } from 'utils/auth/applyDealerIdFromUrl';
import { ensureDealerAccess } from 'state/dealerAccess';

/**
 * SecureOutlet is a wrapper around the react-router Outlet component.
 * It performs additional checks to verify if the current tenant is allowed to access 3D Exhibition.
 */
export const SecureOutlet = () => {
  const throwError = useError();
  const [isValidating, setIsValidating] = React.useState(true);

  React.useEffect(() => {
    const validateAccess = async () => {
      try {
        await tenantConfig().configLoaded;

        // If the user is not authenticated, we do not need to verify the whitelist yet
        // With the early return, we will loop back to let the user complete the authentication flow
        // Once the user is authenticated, the dealer access can be evaluated properly
        if (!isAuthenticated()) {
          setIsValidating(false);
          return;
        }

        if (tenantConfig().inactive === true) {
          throwError(EErrorCode.DISABLED_MARKET);
          return;
        }

        // Apply the dealerId selected in AVP Portal (via URL param) before the whitelist check.
        // This ensures the whitelist is evaluated against the dealer the user actually selected,
        // not the first dealer in their list (which is the default before the URL param is applied).
        await applyDealerIdFromUrl(user);

        const accessResult = await ensureDealerAccess();

        if (accessResult.status === 'denied') {
          throwError(accessResult.code ?? EErrorCode.DEALER_NOT_WHITELISTED);
          return;
        }

        if (accessResult.status === 'failed') {
          throwError(accessResult.code ?? EErrorCode.DEALER_ACCESS_CHECK_FAILED);
          return;
        }

        // Validation complete - allow rendering
        setIsValidating(false);
      } catch {
        throwError(EErrorCode.DISABLED_MARKET);
      }
    };

    validateAccess();
  }, [throwError]);

  // Block rendering until validation completes
  if (isValidating) {
    return null;
  }

  return <Outlet />;
};
